'use client';

import classNames from 'classnames';
import { ComponentProps, useCallback, useMemo } from 'react';
import { FormattedTime, useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import { millisecondsInDay } from '@alltrails/data-formatting/dateConstants';
import IconButton from '@alltrails/denali/components/IconButton';
import Pause from '@alltrails/denali/icons/Pause';
import Play from '@alltrails/denali/icons/Play';
import { addPreloadedOverlayUTC, updateIsPlayingOverlay, updatePreloadedOverlayUTCs, updateSelectedUTC } from '../../redux/conditionsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/helpers';
import { getLocalizedDateStart, getLocalizedUTCDate, getUTCFromLocalized } from '../../utils/timeHelpers';
import DaySelect from '../DaySelect';
import TimeScrubber from '../TimeScrubber';
import styles from './DayAndTimeControls.module.scss';

type DayAndTimeControlsProps = {
  compact?: boolean;
  days: { tempHigh?: number; tempLow?: number; weatherIconCode?: number }[];
  hideTime?: boolean;
  intervalMinutes: number;
  onDayChange?: () => void;
  onPause?: () => void;
  onPlay?: () => void;
  onTimeChange?: () => void;
  utcOffsetSeconds: number;
};

const DayAndTimeControls = ({
  compact,
  days,
  hideTime,
  intervalMinutes,
  onDayChange,
  onPause,
  onPlay,
  onTimeChange,
  utcOffsetSeconds
}: DayAndTimeControlsProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { isPlayingOverlay, selectedUTC } = useAppSelector(state => ({
    isPlayingOverlay: state.conditions.isPlayingOverlay,
    selectedUTC: state.conditions.selectedUTC
  }));

  const utcTodayStart = useMemo(() => getLocalizedDateStart(Date.now(), utcOffsetSeconds), [utcOffsetSeconds]);

  const daySelectDays = useMemo(() => {
    return days.map<ComponentProps<typeof DaySelect>['days'][number]>((day, index) => ({
      ...day,
      // The start of each day, localized to the display timezone
      utcMs: utcTodayStart + millisecondsInDay * index
    }));
  }, [days, utcTodayStart]);

  const onDaySelect = useCallback(
    (localizedUTC: number) => {
      onDayChange?.();

      // Pause overlay playback
      dispatch(updateIsPlayingOverlay(false));

      const newSelectedUTC = getUTCFromLocalized(localizedUTC, utcOffsetSeconds);
      dispatch(updateSelectedUTC(newSelectedUTC));

      // Clear the preloaded overlays when the local date changes
      dispatch(updatePreloadedOverlayUTCs([newSelectedUTC]));
    },
    [dispatch, onDayChange, utcOffsetSeconds]
  );

  const onTimeScrub = useCallback(
    (localizedUTC: number) => {
      onTimeChange?.();

      // Pause overlay playback
      dispatch(updateIsPlayingOverlay(false));

      const newSelectedUTC = getUTCFromLocalized(localizedUTC, utcOffsetSeconds);
      dispatch(updateSelectedUTC(newSelectedUTC));

      // Add the new time to the list of preloaded overlays so that scrubbing is smoother once a time has been viewed
      dispatch(addPreloadedOverlayUTC(newSelectedUTC));
    },
    [dispatch, onTimeChange, utcOffsetSeconds]
  );

  if (!selectedUTC) {
    return null;
  }

  if (hideTime) {
    return (
      <div className={styles.dayOnlyContainer}>
        <div className={classNames(styles.daySelectContainer, styles.dayOnly)}>
          <DaySelect
            days={daySelectDays}
            onChange={onDaySelect}
            placement="top-start"
            selectedUTCMs={getLocalizedDateStart(selectedUTC, utcOffsetSeconds)}
            size={compact ? 'sm' : 'md'}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.daySelectContainer}>
        <DaySelect
          days={daySelectDays}
          onChange={onDaySelect}
          placement="top-start"
          selectedUTCMs={getLocalizedDateStart(selectedUTC, utcOffsetSeconds)}
          size={compact ? 'sm' : 'md'}
        />
        <Typography className={styles.lowercase} variant="text100">
          {/* Show the time in the display timezone */}
          <FormattedTime value={getLocalizedUTCDate(selectedUTC, utcOffsetSeconds)} timeZone="UTC" />
        </Typography>
      </div>
      <div className={styles.timeScrubberContainer}>
        <TimeScrubber
          ariaLabel={intl.formatMessage({ defaultMessage: 'Time' })}
          intervalMinutes={intervalMinutes}
          onChange={onTimeScrub}
          selectedUTCMs={getLocalizedUTCDate(selectedUTC, utcOffsetSeconds).getTime()}
        />
      </div>
      <IconButton
        icon={{ Component: isPlayingOverlay ? Pause : Play }}
        onClick={() => {
          if (isPlayingOverlay) {
            onPause?.();
            dispatch(updateIsPlayingOverlay(false));
          } else {
            onPlay?.();
            dispatch(updateIsPlayingOverlay(true));
          }
        }}
        size={compact ? 'sm' : 'md'}
        testId="conditions-overlay-play"
        title={isPlayingOverlay ? intl.formatMessage({ defaultMessage: 'Pause' }) : intl.formatMessage({ defaultMessage: 'Play' })}
        variant="flat"
      />
    </div>
  );
};

export default DayAndTimeControls;
